/**
 * Starting a round that has been PAID for.
 *
 * A taker's stake leaves their balance the moment the server answers the take; the game starts a beat
 * later, on the client, and can fail to in ways the server never hears about (the table is still
 * loading, a throw before the session request goes out). Two rules keep that from being a charge with
 * nothing behind it:
 *
 *   1. Do not charge until the table exists (`whenReady`) — a slow first load is then a wait or a plain
 *      "still loading, you have not been charged", never a paid seat and a spinner.
 *   2. Once charged, a round that does not start says so (`guardStart`): a rejected start, or none within
 *      a deadline, ends in a message and a way back to the seat, not a spinner that never stops.
 *
 * Deliberately NOT here: retrying the session request. The server marks the seat started on the first
 * successful open, so a retry after a lost reply is refused as "already played" and burns the seat.
 */

/** Resolves true once `isReady()` holds, false if it has not within `timeoutMs`. Polls; never rejects. */
export function whenReady(isReady: () => boolean, timeoutMs: number, pollMs = 100): Promise<boolean> {
  if (isReady()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let waited = 0;
    const timer = setInterval(() => {
      waited += pollMs;
      if (isReady()) {
        clearInterval(timer);
        resolve(true);
      } else if (waited >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, pollMs);
  });
}

export interface StartGuard {
  /** Begin the round. May reject. */
  start: () => Promise<unknown>;
  /** Subscribe to "the round has started"; returns the unsubscribe. */
  onStarted: (listener: () => void) => () => void;
  /** How long a paid round may take to start before it is reported. */
  timeoutMs: number;
  /** Told once, with a short reason, if the round did not start. */
  failed: (reason: string) => void;
}

/**
 * Run `start`, and make sure the outcome is either a started round or exactly one `failed` call.
 * A late start (after `failed`) is still welcome — the game simply begins over whatever was shown.
 */
export function guardStart(guard: StartGuard): void {
  let settled = false;
  let unsubscribe: () => void = () => undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const finish = (): void => {
    settled = true;
    if (timer !== undefined) clearTimeout(timer);
    unsubscribe();
  };
  const fail = (reason: string): void => {
    if (settled) return;
    finish();
    guard.failed(reason);
  };

  unsubscribe = guard.onStarted(() => {
    if (!settled) finish();
  });
  timer = setTimeout(() => fail('it took too long'), guard.timeoutMs);

  guard.start().catch((error: unknown) => {
    fail(error instanceof Error && error.message ? error.message : 'something went wrong');
  });
}

/** A ranked round that could not be started, with a reason fit to show. Thrown by the start, read by whoever launched it. */
export class RoundNotOpened extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoundNotOpened';
  }
}

/**
 * Launch a start that nobody is guarding (`guardStart` is for a seat that was paid for, which has its own page to
 * return to): a start that could not open is told to `notOpened` with its reason, so the table is never left blank
 * with nothing said. Any other error is not this function's to swallow: it is rethrown, unhandled, as a bug.
 */
export function launchStart(start: () => Promise<unknown>, notOpened: (reason: string) => void): void {
  start().catch((error: unknown) => {
    if (error instanceof RoundNotOpened) notOpened(error.message);
    else throw error;
  });
}
